import React, { useState } from "react";
import { GetApplicantResume, GetPreApplicantResume } from "Services/OtherApis";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { isArabic } from "constants/isArabic";
import { IoEyeOutline } from "react-icons/io5";
import { MdOutlineFileDownload } from "react-icons/md";
import DownloadResume from "functions/download";
import OpenResumeInNewTab from "functions/openFileInNewTab";
import ExportExcel from "functions/exportExcel";

function Table({
  data,
  loading,
  setDropDown,
  dropdown,
  dataTitles,
  isPreApplicants = false,
  isDropDown = false,
}) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const [language, setSelectedLanguage] = useState(isRTL ? "ar" : "en");
  const [loadingResumes, setLoadingResumes] = useState({}); // Loading state for downloads
  const [loadingPreviews, setLoadingPreviews] = useState({}); // Loading state for previews

  const exportToExcel = () => {
    const headers = [
      "Job Name",
      "First Name",
      "Father Name",
      "Family Name",
      "Nationality",
      "Degree",
      "Major",
      "Experience",
      "Residence",
      "Email",
      "Phone",
      "Notice Period",
      "Date of Birth",
    ];

    const formattedData = data.map(
      ({ updatedAt, __v, resumeFile, jobId, _id, ...rest }) => ({
        "Job Name": isPreApplicants
          ? rest?.jobTitle
          : jobId?.jobTitle?.[language] ||
            jobId?.jobTitle?.en ||
            jobId?.jobTitle?.ar ||
            "N/A",
        "First Name": rest.firstName,
        "Father Name": rest.fatherName,
        "Family Name": rest.familyName,
        Nationality: rest.nationality,
        Degree: rest.degree,
        Major: rest.major,
        Experience: rest.yearsOfExperience,
        Residence: rest.placeOfResidence,
        Email: rest.email,
        Phone: rest.phoneNumber,
        "Notice Period": rest.noticePeriod,
        "Date of Birth": rest.dateOfBirth,
      })
    );

    ExportExcel({
      fileName: "Applicants",
      formattedData,
      headers,
    });
  };

  async function getResume(applicantId, isPreview = false, name) {
    if (isPreview) {
      setLoadingPreviews((prev) => ({ ...prev, [applicantId]: true })); // Start preview loading
    } else {
      setLoadingResumes((prev) => ({ ...prev, [applicantId]: true })); // Start download loading
    }
    let res;
    try {
      if (isPreApplicants) {
        res = await GetPreApplicantResume(applicantId);
      } else {
        res = await GetApplicantResume(applicantId);
      }

      const resumeData = res?.resumeFile;

      if (!resumeData) {
        toast.error("No resume file available");
        return;
      }

      if (isPreview) {
        OpenResumeInNewTab(resumeData);
      } else {
        DownloadResume(resumeData, `${name} Resume`);
      }
    } catch (e) {
      toast.error("Error fetching resume");
    } finally {
      if (isPreview) {
        setLoadingPreviews((prev) => ({ ...prev, [applicantId]: false })); // Stop preview loading
      } else {
        setLoadingResumes((prev) => ({ ...prev, [applicantId]: false })); // Stop download loading
      }
    }
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center">
        {isDropDown ? (
          <select
            onChange={(e) => setDropDown(e.target.value)}
            name="jobType"
            value={dropdown}
            className={`w-[400px] mt-1 p-2   border-gray-300  bg-gray-50 rounded-lg shadow-sm border  ${
              isRTL ? "text-right pr-10 pl-5" : ""
            }`}
          >
            <option value="All">{t("All Jobs")}</option>
            {dataTitles
              ?.filter((v) => v?.jobTitle?.[language])
              ?.map((v, k) => {
                return (
                  <option value={v?._id}>{v?.jobTitle?.[language]}</option>
                );
              })}
          </select>
        ) : (
          <div className="font-semibold text-xl  tracking-wide">
            {t("Open Applicants")}
          </div>
        )}

        {data?.length > 0 && (
          <button
            onClick={exportToExcel}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg shadow-md hover:bg-blue-600"
          >
            {t("Export to Excel")}
          </button>
        )}
      </div>
      {loading ? (
        <p className="text-gray-600 mt-4 text-center border border-gray-300  py-24 text-lg rounded-md">
          {t("Loading Jobs")} ...
        </p>
      ) : data?.length === 0 ? (
        <p className="text-gray-600 mt-4 text-center border border-gray-300  py-24 text-lg rounded-md">
          {t("No Jobs Available")} !
        </p>
      ) : (
        <div
          className={`overflow-x-auto relative  mt-4 bg-white p-3 rounded-lg `}
        >
          <table
            className={`w-full whitespace-nowrap  text-sm   ${
              isRTL ? "text-right" : "text-left"
            }`}
          >
            <thead className="text-md text-gray-500  bg-slate-100  font-normal">
              <th className=" px-4 py-4">{t("Job Title")}</th>
              <th className=" px-4 py-4">{t("First Name")}</th>
              <th className=" px-4 py-4">{t("Family Name")}</th>
              <th className=" px-4 py-4">{t("Nationality")}</th>
              <th className=" px-4 py-4">{t("Degree")}</th>
              <th className=" px-4 py-4">{t("Major")}</th>
              <th className=" px-4 py-4">{t("Experience")}</th>
              <th className=" px-4 py-4">{t("Residence")}</th>
              <th className=" px-4 py-4">{t("Email")}</th>
              <th className=" px-4 py-4">{t("Phone")}</th>
              <th className=" px-4 py-4">{t("Notice Period")}</th>
              <th className=" px-4 py-4">{t("Date of Birth")}</th>
              <th className=" px-4 py-4">{t("Action")}</th>
            </thead>
            <tbody
              className={`text-gray-500 ${language === "ar" && "text-lg"}`}
            >
              {data?.map((applicant, k) => (
                <tr
                  key={applicant._id}
                  className={`odd:bg-white even:bg-gray-50 ${
                    k % 2
                      ? "bg-gray-50 dark:bg-gray-600"
                      : "bg-white dark:bg-gray-700"
                  }}`}
                >
                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant?.jobTitle) ||
                      isArabic(applicant?.jobId?.jobTitle[language])
                        ? " Majalla text-lg"
                        : ""
                    }`}
                  >
                    {applicant?.jobTitle ||
                      applicant?.jobId?.jobTitle[language] ||
                      applicant?.jobId?.jobTitle.en ||
                      applicant?.jobId?.jobTitle.ar}
                  </td>
                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant.firstName) ? " Majalla text-lg" : ""
                    }`}
                  >
                    {applicant.firstName + " " + applicant.fatherName}
                  </td>

                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant.familyName) ? " Majalla text-lg" : ""
                    }`}
                  >
                    {applicant.familyName}
                  </td>

                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant.nationality) ? " Majalla text-lg" : ""
                    }`}
                  >
                    {applicant.nationality}
                  </td>
                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant.degree) ? " Majalla text-lg" : ""
                    }`}
                  >
                    {applicant.degree}
                  </td>
                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant.major) ? " Majalla text-lg" : ""
                    }`}
                  >
                    {applicant.major}
                  </td>

                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant.yearsOfExperience)
                        ? " Majalla text-lg"
                        : ""
                    }`}
                  >
                    {applicant.yearsOfExperience || "-"}
                  </td>

                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant.placeOfResidence)
                        ? " Majalla text-lg"
                        : ""
                    }`}
                  >
                    {applicant.placeOfResidence}
                  </td>
                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant.email) ? " Majalla text-lg" : ""
                    }`}
                  >
                    {applicant.email}
                  </td>
                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant.phoneNumber) ? " Majalla text-lg" : ""
                    }`}
                  >
                    {applicant.phoneNumber}
                  </td>

                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant.noticePeriod) ? " Majalla text-lg" : ""
                    }`}
                  >
                    {applicant.noticePeriod}
                  </td>
                  <td
                    className={`border-b border-gray-300 px-4 py-4 ${
                      isArabic(applicant.dateOfBirth) ? " Majalla text-lg" : ""
                    }`}
                  >
                    {applicant.dateOfBirth}
                  </td>
                  <td className=" items-center border-b border-gray-300 px-4 py-4 flex flex-row  ">
                    <button
                      onClick={() => getResume(applicant?._id, true)}
                      className="text-gray-500 rounded-lg  cursor-pointer duration-300 hover:text-blue-600 flex items-center  w-10  "
                      disabled={loadingPreviews[applicant?._id]} // Disable only this button when clicked
                    >
                      {loadingPreviews[applicant?._id] ? (
                        <div className="flex items-center justify-center">
                          {/* <a>{t("Loading")} ... </a> */}

                          <svg
                            className="animate-spin h-7 w-7  text-white"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="white"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="white"
                              d="M4 12a8 8 0 018-8v8H4z"
                            ></path>
                          </svg>
                        </div>
                      ) : (
                        <div className="">
                          <a>
                            {" "}
                            <IoEyeOutline className="text-[25px]" />
                          </a>
                        </div>
                      )}
                    </button>
                    <button
                      onClick={() =>
                        getResume(applicant?._id, false, applicant.firstName)
                      }
                      className="text-gray-500 rounded-lg  cursor-pointer duration-300 hover:text-blue-600 flex items-center  w-10 "
                      disabled={loadingResumes[applicant?._id]} // Disable only this button when clicked
                    >
                      {loadingResumes[applicant?._id] ? (
                        <div className="flex items-center justify-center">
                          <svg
                            className="animate-spin h-7 w-7 mr-2 text-white"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="white"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="white"
                              d="M4 12a8 8 0 018-8v8H4z"
                            ></path>
                          </svg>
                        </div>
                      ) : (
                        <div className="">
                          <a>
                            {" "}
                            <MdOutlineFileDownload className="text-[28px] " />
                          </a>
                        </div>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Table;
